import React, { useEffect, useState, useMemo } from 'react';
import { dailyPlansAPI } from '../services/api';
import '../styles/myLogs.css';   // reuses all shared + calendar styles
import { useNavigate } from 'react-router-dom';

/* ── Helpers (same as myLogs) ── */
function fmtDate(dateStr) {
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString('default', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtDay(dateStr) {
    const d = new Date(dateStr);
    if (isNaN(d)) return '';
    return d.toLocaleDateString('default', { weekday: 'short' });
}
function sumMinutes(tasks = []) {
    return tasks.reduce((s, t) => s + Math.round((parseFloat(t.actualHours) || 0) * 60), 0);
}
function fmtHrs(mins) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0 && m === 0) return '—';
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
}
function toISO(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function fmt12(timeStr) {
    if (!timeStr) return '—';

    // Convert UTC to IST
    const date = new Date(timeStr);

    return date.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata'  
    });
}

/* ─────────────────────────────────────────
   Day Detail Modal (same as before)
───────────────────────────────────────── */
function DayDetailModal({ dateKey, data, onClose }) {
    const label   = fmtDate(dateKey);
    const dayName = fmtDay(dateKey);
    const tasks   = data?.tasks || [];
    const totalMins = sumMinutes(tasks);

    return (
        <div className="cal-overlay" onClick={onClose}>
            <div className="cal-detail-modal" onClick={e => e.stopPropagation()}>
                <div className="cal-detail-header">
                    <div>
                        <div className="cal-detail-day">{dayName}</div>
                        <div className="cal-detail-date">{label}</div>
                    </div>
                    <button className="cal-close-btn" onClick={onClose}>✕</button>
                </div>

                {/* Check-in / Check-out */}
                <div className="cal-detail-inout">
                    <div className="cal-inout-pill in">
                        <span className="cal-inout-label">CHECK IN</span>
                        <span className="cal-inout-time">{fmt12(data?.checkIn) || '—'}</span>
                    </div>
                    <div className="cal-inout-arrow">→</div>
                    <div className="cal-inout-pill out">
                        <span className="cal-inout-label">CHECK OUT</span>
                        <span className="cal-inout-time">{fmt12(data?.checkOut) || '—'}</span>
                    </div>
                    <div className="cal-inout-total">
                        <span className="cal-inout-label">TOTAL</span>
                        <span className="cal-inout-time purple">{fmtHrs(totalMins)}</span>
                    </div>
                </div>

                {/* Tasks table */}
                {tasks.length > 0 ? (
                    <div className="cal-detail-table-wrap">
                        <table className="cal-detail-table">
                            <thead>
                                <tr>
                                    <th>Task</th>
                                    <th>Project</th>
                                    <th>Logs</th>
                                    <th>Status</th>
                                    <th>Hours</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tasks.map(t => {
                                    const project = t.backlogTask?.project?.name || 'Free Time';
                                    const mins = Math.round((parseFloat(t.actualHours) || 0) * 60);
                                    return (
                                        <tr key={t.id}>
                                            <td className="dt-task">{t.title}</td>
                                            <td><span className="log-project-badge">{project}</span></td>
                                            <td className="dt-notes">{t.notes || <span className="log-notes-empty">—</span>}</td>
                                            <td>
                                                <span className={`log-status-dot ${t.completed ? 'Completed' : 'pending'}`}>
                                                    {t.completed ? 'Completed' : 'Pending'}
                                                </span>
                                            </td>
                                            <td className="dt-hrs">
                                                <span className={`log-hrs ${mins > 0 ? 'has' : 'nil'}`}>{fmtHrs(mins)}</span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="cal-detail-empty">No tasks logged for this day.</div>
                )}
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────
   Calendar Grid (inline, not a modal now)
───────────────────────────────────────── */
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function CalendarGrid({ calendarData }) {
    const today = new Date();
    const [viewYear,  setViewYear]  = useState(today.getFullYear());
    const [viewMonth, setViewMonth] = useState(today.getMonth());
    const [detailKey, setDetailKey] = useState(null);

    const monthLabel = new Date(viewYear, viewMonth, 1)
        .toLocaleString('default', { month: 'long', year: 'numeric' });

    const prevMonth = () => {
        if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
        else setViewMonth(m => m - 1);
    };
    const nextMonth = () => {
        if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
        else setViewMonth(m => m + 1);
    };

    const cells = useMemo(() => {
        const firstDay    = new Date(viewYear, viewMonth, 1).getDay();
        const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
        const arr = [];
        for (let i = 0; i < firstDay; i++) arr.push(null);
        for (let d = 1; d <= daysInMonth; d++) arr.push(d);
        while (arr.length % 7 !== 0) arr.push(null);
        return arr;
    }, [viewYear, viewMonth]);

    const todayISO = toISO(today);

    return (
        <>
            {/* Month nav */}
            <div className="cal-nav cal-nav-page">
                <button className="cal-nav-btn" onClick={prevMonth}>‹</button>
                <span className="cal-nav-label">{monthLabel}</span>
                <button className="cal-nav-btn" onClick={nextMonth}>›</button>
            </div>

            {/* Grid */}
            <div className="cal-grid cal-grid-page">
                {DAYS.map(d => (
                    <div key={d} className="cal-day-header">{d}</div>
                ))}

                {cells.map((day, idx) => {
                    if (!day) return <div key={`blank-${idx}`} className="cal-cell empty" />;

                    const dateKey = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const data    = calendarData[dateKey];
                    const isToday = dateKey === todayISO;
                    const hasData = !!data;

                    return (
                        <div
                            key={dateKey}
                            className={`cal-cell${hasData ? ' has-data' : ''}${isToday ? ' today' : ''}`}
                            onClick={() => hasData && setDetailKey(dateKey)}
                        >
                            <div className={`cal-cell-num${isToday ? ' today-num' : ''}`}>{day}</div>
                            {hasData && (
                                <div className="cal-cell-badges">
                                    <div className="cal-badge in">IN : {fmt12(data?.checkIn) || '—'}</div>
                                    <div className="cal-badge out">OUT : {fmt12(data?.checkOut) || '—'}</div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Legend */}
            <div className="cal-legend">
                <span className="cal-legend-dot in" /> Check In &nbsp;&nbsp;
                <span className="cal-legend-dot out" /> Check Out &nbsp;&nbsp;
                <span className="cal-legend-dot today-dot" /> Today
            </div>

            {/* Day detail modal */}
            {detailKey && (
                <DayDetailModal
                    dateKey={detailKey}
                    data={calendarData[detailKey]}
                    onClose={() => setDetailKey(null)}
                />
            )}
        </>
    );
}

/* ─────────────────────────────────────────
   Calendar Page
───────────────────────────────────────── */
const CalendarPage = () => {
    const navigate = useNavigate();
    const [plans,   setPlans]   = useState([]);
    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState(null);

    useEffect(() => { loadPlans(); }, []);

    const loadPlans = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await dailyPlansAPI.getMyPlans();
            setPlans(res.data);
        } catch (err) {
            console.error(err);
            setError('Failed to load attendance data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const calendarData = useMemo(() => {
        const map = {};
        plans.forEach(p => {
            map[p.date] = {
                checkIn:  p.checkInTime,
                checkOut: p.checkOutTime,
                tasks:    p.tasks || [],
            };
        });
        return map;
    }, [plans]);

    const totalDays = plans.length;
    const totalMins = plans.reduce((s, p) => s + sumMinutes(p.tasks), 0);

    return (
        <div className="logs-page">

            {/* ── Header — same gradient style as My Logs ── */}
            <div className="logs-page-header">
                <div className="logs-header-top">
                    <button className="logs-back-btn" onClick={() => navigate('/my-logs')}>← Back</button>
                    <div className="logs-title-block">
                        <h1 className="logs-page-title">📅 Attendance Calendar</h1>
                        <p className="logs-page-sub">Your monthly check-in & check-out history</p>
                    </div>
                    <div style={{ width: 80 }} />
                </div>

                {!loading && !error && (
                    <div className="logs-stats-strip">
                        <div className="logs-stat-pill">📅 <strong>{totalDays}</strong>&nbsp;Days Logged</div>
                        <div className="logs-stat-pill">⏱ <strong>{fmtHrs(totalMins)}</strong>&nbsp;Total Hours</div>
                    </div>
                )}
            </div>

            {/* ── Body ── */}
            <div className="logs-body">

                {/* Loading */}
                {loading && [1, 2, 3].map(i => (
                    <div key={i} className="logs-skeleton" style={{ animationDelay: `${i * 60}ms`, height: 60 }} />
                ))}

                {/* Error */}
                {!loading && error && (
                    <div className="logs-error">
                        <span className="logs-error-text">⚠️ {error}</span>
                        <button className="logs-retry-btn" onClick={loadPlans}>Retry</button>
                    </div>
                )}

                {/* Calendar */}
                {!loading && !error && (
                    <div className="cal-page-card">
                        <CalendarGrid calendarData={calendarData} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default CalendarPage;